import React, { useState, useEffect } from 'react';
import { api } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { 
  AlertCircle, CalendarDays, Users, UserX, FileText, Download,
  ChevronLeft, ChevronRight, RefreshCw, User
} from 'lucide-react';
import { toast } from 'sonner';

const AttendanceReports = () => {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('unmarked');
  const [selectedDate, setSelectedDate] = useState(() => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return yesterday.toISOString().split('T')[0];
  });
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [unmarkedData, setUnmarkedData] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);

  useEffect(() => {
    if (activeTab === 'unmarked') {
      fetchUnmarkedAttendance();
    } else {
      fetchMonthlySummary();
    }
  }, [activeTab, selectedDate, selectedMonth, selectedYear]);

  const fetchUnmarkedAttendance = async () => {
    setLoading(true);
    try {
      const res = await api.getUnmarkedAttendance(selectedDate);
      setUnmarkedData(res.data);
    } catch (error) {
      console.error('Error fetching unmarked attendance:', error);
      toast.error('Failed to load unmarked attendance');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlySummary = async () => {
    setLoading(true);
    try {
      const res = await api.getMonthlyAttendanceSummary(selectedMonth, selectedYear);
      setMonthlySummary(res.data);
    } catch (error) {
      console.error('Error fetching monthly summary:', error);
      toast.error('Failed to load monthly summary');
    } finally {
      setLoading(false);
    }
  };

  const changeDate = (days) => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + days);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const changeMonth = (delta) => {
    let newMonth = selectedMonth + delta;
    let newYear = selectedYear;
    
    if (newMonth > 12) {
      newMonth = 1;
      newYear++;
    } else if (newMonth < 1) {
      newMonth = 12;
      newYear--;
    }
    
    setSelectedMonth(newMonth);
    setSelectedYear(newYear);
  };

  const getMonthName = (month) => {
    return new Date(2000, month - 1).toLocaleString('default', { month: 'long' });
  };

  const exportToCSV = () => {
    if (!monthlySummary || !monthlySummary.employees.length) {
      toast.error('No data to export');
      return;
    }

    const headers = ['Employee Name', 'Employee Code', 'Present', 'Absent', 'Half Day', 'WFH', 'Overtime', 'Total Days', 'Overtime Hours'];
    const rows = monthlySummary.employees.map(emp => [
      emp.employee_name,
      emp.employee_code || '',
      emp.present,
      emp.absent,
      emp.half_day,
      emp.work_from_home,
      emp.overtime,
      emp.total_days,
      emp.overtime_hours
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance_${getMonthName(selectedMonth)}_${selectedYear}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Attendance Reports</h1>
          <p className="text-slate-500">View attendance reports and unmarked attendance alerts</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b">
        <button
          onClick={() => setActiveTab('unmarked')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'unmarked'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-unmarked"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4" />
            Unmarked Attendance
          </div>
        </button>
        <button
          onClick={() => setActiveTab('monthly')}
          className={`px-4 py-2 font-medium text-sm transition-colors ${
            activeTab === 'monthly'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
          data-testid="tab-monthly"
        >
          <div className="flex items-center gap-2">
            <CalendarDays className="w-4 h-4" />
            Monthly Summary
          </div>
        </button>
      </div>

      {/* Unmarked Attendance Tab */}
      {activeTab === 'unmarked' && (
        <div className="space-y-4">
          {/* Date Selector */}
          <div className="flex items-center gap-2 bg-white p-3 rounded-lg shadow-sm border w-fit">
            <Button variant="ghost" size="sm" onClick={() => changeDate(-1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            
            <div className="flex items-center gap-2 px-3">
              <CalendarDays className="w-5 h-5 text-indigo-600" />
              <input
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                max={new Date().toISOString().split('T')[0]}
                className="border-0 focus:ring-0 font-medium text-slate-900"
                data-testid="unmarked-date-input"
              />
            </div>
            
            <Button variant="ghost" size="sm" onClick={() => changeDate(1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
            
            <Button variant="ghost" size="sm" onClick={fetchUnmarkedAttendance}>
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
          ) : unmarkedData && (
            <>
              {/* Summary Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Users className="w-5 h-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Total Team Members</p>
                        <p className="text-xl font-bold text-slate-900">{unmarkedData.total_team_members || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <User className="w-5 h-5 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Marked</p>
                        <p className="text-xl font-bold text-slate-900">{unmarkedData.marked_count || 0}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className={unmarkedData.total > 0 ? 'border-red-200 bg-red-50' : ''}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className={`p-2 rounded-lg ${unmarkedData.total > 0 ? 'bg-red-100' : 'bg-slate-100'}`}>
                        <UserX className={`w-5 h-5 ${unmarkedData.total > 0 ? 'text-red-600' : 'text-slate-600'}`} />
                      </div>
                      <div>
                        <p className="text-sm text-slate-500">Unmarked</p>
                        <p className={`text-xl font-bold ${unmarkedData.total > 0 ? 'text-red-600' : 'text-slate-900'}`}>
                          {unmarkedData.total || 0}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Unmarked List */}
              {unmarkedData.total > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-red-600">
                      <AlertCircle className="w-5 h-5" />
                      Employees with Unmarked Attendance ({unmarkedData.date})
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b">
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Employee</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Code</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Team</th>
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Team Leader</th>
                          </tr>
                        </thead>
                        <tbody>
                          {unmarkedData.unmarked.map((emp, idx) => (
                            <tr key={idx} className="border-b last:border-0 hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium text-slate-900">{emp.employee_name}</p>
                                  <p className="text-xs text-slate-500">{emp.email}</p>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-slate-600">{emp.employee_code || '-'}</td>
                              <td className="py-3 px-4 text-slate-600">{emp.team_name}</td>
                              <td className="py-3 px-4 text-slate-600">{emp.team_leader}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-12">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto bg-green-100 rounded-full flex items-center justify-center mb-4">
                        <Users className="w-8 h-8 text-green-600" />
                      </div>
                      <h3 className="text-lg font-medium text-slate-900 mb-2">All Attendance Marked</h3>
                      <p className="text-slate-500">All team members have their attendance marked for {unmarkedData.date}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}

      {/* Monthly Summary Tab */}
      {activeTab === 'monthly' && (
        <div className="space-y-4">
          {/* Month Selector */}
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2 bg-white p-3 rounded-lg shadow-sm border">
              <Button variant="ghost" size="sm" onClick={() => changeMonth(-1)}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              
              <div className="flex items-center gap-2 px-3 min-w-[180px] justify-center">
                <CalendarDays className="w-5 h-5 text-indigo-600" />
                <span className="font-medium text-slate-900">
                  {getMonthName(selectedMonth)} {selectedYear}
                </span>
              </div>
              
              <Button variant="ghost" size="sm" onClick={() => changeMonth(1)}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              
              <Button variant="ghost" size="sm" onClick={fetchMonthlySummary}>
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>

            <Button 
              variant="outline" 
              onClick={exportToCSV}
              disabled={!monthlySummary?.employees?.length}
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-indigo-600"></div>
            </div>
          ) : monthlySummary && (
            <>
              {/* Summary Card */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-100 rounded-lg">
                      <FileText className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Total Employees with Records</p>
                      <p className="text-xl font-bold text-slate-900">{monthlySummary.total_employees || 0}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Summary Table */}
              {monthlySummary.employees?.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CalendarDays className="w-5 h-5" />
                      Attendance Summary - {getMonthName(selectedMonth)} {selectedYear}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-slate-50">
                            <th className="text-left py-3 px-4 text-sm font-medium text-slate-600">Employee</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-green-600">Present</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-red-600">Absent</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-amber-600">Half Day</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-blue-600">WFH</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-purple-600">Overtime</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-slate-600">Total Days</th>
                            <th className="text-center py-3 px-4 text-sm font-medium text-purple-600">OT Hours</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlySummary.employees.map((emp, idx) => (
                            <tr key={idx} className="border-b last:border-0 hover:bg-slate-50">
                              <td className="py-3 px-4">
                                <div>
                                  <p className="font-medium text-slate-900">{emp.employee_name}</p>
                                  {emp.employee_code && (
                                    <p className="text-xs text-slate-500">{emp.employee_code}</p>
                                  )}
                                </div>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-sm font-medium">
                                  {emp.present}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className={`px-2 py-1 rounded text-sm font-medium ${
                                  emp.absent > 0 ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-500'
                                }`}>
                                  {emp.absent}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className={`px-2 py-1 rounded text-sm font-medium ${
                                  emp.half_day > 0 ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'
                                }`}>
                                  {emp.half_day}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className={`px-2 py-1 rounded text-sm font-medium ${
                                  emp.work_from_home > 0 ? 'bg-blue-100 text-blue-700' : 'bg-slate-100 text-slate-500'
                                }`}>
                                  {emp.work_from_home}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4">
                                <span className={`px-2 py-1 rounded text-sm font-medium ${
                                  emp.overtime > 0 ? 'bg-purple-100 text-purple-700' : 'bg-slate-100 text-slate-500'
                                }`}>
                                  {emp.overtime}
                                </span>
                              </td>
                              <td className="text-center py-3 px-4 font-medium text-slate-900">
                                {emp.total_days}
                              </td>
                              <td className="text-center py-3 px-4">
                                {emp.overtime_hours > 0 ? (
                                  <span className="text-purple-600 font-medium">{emp.overtime_hours}h</span>
                                ) : (
                                  <span className="text-slate-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              ) : (
                <Card>
                  <CardContent className="py-12">
                    <div className="text-center">
                      <CalendarDays className="w-12 h-12 mx-auto text-slate-400 mb-4" />
                      <h3 className="text-lg font-medium text-slate-900 mb-2">No Attendance Records</h3>
                      <p className="text-slate-500">No attendance records found for {getMonthName(selectedMonth)} {selectedYear}</p>
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AttendanceReports;
